/**
 * Aura nails — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_nail_looks`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const auraNailsPack: ToilePack = {
    slug: 'aura-nails',
    category: 'beauty',
    name: 'Aura nails',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'pink', label: 'Pink glow', subject: 'short almond nails in milky white with a blurred hot-pink glow in the centre of each nail'},
        {key: 'twotone', label: 'Two-tone', subject: 'oval nails with a purple centre glowing out through blue to a pale edge'},
        {key: 'chrome', label: 'Aura chrome', subject: 'almond nails with a soft orange and yellow aura under a sheer pearl chrome finish'},
    ],
    styles: [
        {key: 'haze', label: 'Pastel haze', style: 'on a pastel lilac surface in soft hazy daylight from camera-left, a bright airy balance so the blurred glow reads out to its edge', palette: 'Lilac and pale pink around the hand, the aura colour the strongest note', framing: 'The hand resting flat, seen from above, fingertips toward the top of the frame'},
        {key: 'neon', label: 'Neon night', style: 'in a dark room washed by a soft violet and pink glow from camera-right, a dark saturated balance with the nails glowing', palette: 'Violet and magenta glow on the skin, deep shadow behind', framing: 'The hand raised near-frontal at eye height against deep shadow'},
        {key: 'cafe', label: 'Golden café', style: 'holding a clear glass in warm afternoon sun from camera-left, a warm bright balance with soft refracted colour on the skin', palette: 'Warm skin, clear glass and golden light, the aura colours vivid', framing: 'A near-frontal close view at hand height, the café behind a soft blur'},
    ],
    directions: ['green aura', 'french tip', 'short nails'],
    directionHint: 'try: green aura',
    seo: {
        title: 'Aura Nails: Pink Glow, Two-Tone, Aura Chrome',
        metaDescription: 'Nine aura nail designs: a pink center glow, a purple to blue two-tone and an orange aura under pearl chrome, in three settings.',
        h1: 'Aura nails: pink glow, two-tone and aura chrome',
        tagline: 'Nine aura sets: three glows, each shown in pastel haze, a neon night and golden café light.',
        intro: 'Aura nails for people who want a soft glow of color in the center of each nail that blurs out to the edge. The three designs are short almond nails in milky white with a blurred hot-pink glow, oval nails with a purple center that glows out through blue to a pale edge, and almond nails with a soft orange and yellow aura under a pearl chrome. Type a different glow color and the same blur carries it.',
        useCases: [
            'a pink glow for a soft look',
            'a two-tone aura for a party',
            'a chrome aura for a wedding',
            'a green aura for spring',
        ],
        faq: [
            {q: 'What does aura mean on a nail?', a: 'A color that is strongest at the center of the nail and blurs out toward the edge.'},
            {q: 'How does the two-tone look?', a: 'A purple center that glows out through blue to a pale edge.'},
            {q: 'Can I ask for a different glow?', a: 'Yes. A color you type is a request, not one of the nine pictures shown.'},
        ],
    },
}
